import React, { useMemo } from 'react';
import { observer } from 'mobx-react';
import moment from 'moment';

import Table from '../../../../components/v2/Table';
import { DateFormat } from '../../../../utils/date';
import { useDeadlineNow } from '../../../hooks/useRunWatchdog';
import WebflowDeploy from '../../../Model/WebflowDeploy';
import { DeployDisplayStatus, deriveDeployStatus } from '../../../utils/webflowDeployer';
import texts from '../texts.json';
import { SectionTitle, SiteLink } from '../styles';
import DeployStatusTag from './DeployStatusTag';

const EM_DASH = '—';

/**
 * The earliest deadline among deploys still in progress at `now`: the next moment a row can turn
 * TIMED_OUT without its document changing.
 * @param {Array} docs - WebflowDeploy documents.
 * @param {number} now - The time the rows are derived at.
 * @returns {number|undefined} The deadline in ms, or undefined when no row is waiting on one.
 */
const nextDeadline = (docs, now) => docs
  .filter((doc) => deriveDeployStatus(doc, now) === DeployDisplayStatus.IN_PROGRESS)
  .map((doc) => doc.deadlineAt?.getTime())
  .filter(Boolean)
  .reduce((earliest, deadline) => (earliest === undefined ? deadline : Math.min(earliest, deadline)), undefined);

/**
 * The latest deploys by anyone, newest first, live: a deploy started here or in another tab
 * shows up and moves through its statuses without a refresh.
 */
const DeployHistory = () => {
  const deploys = useMemo(() => WebflowDeploy.recent(), []);
  const { docs } = deploys;

  const columns = useMemo(() => [
    {
      field: 'fileName',
      headerName: texts.history.fileName,
      flex: 1.4,
    },
    {
      field: 'siteName',
      headerName: texts.history.siteName,
      flex: 1,
    },
    {
      field: 'status',
      headerName: texts.history.status,
      flex: 0.8,
      renderCell: ({ value }) => <DeployStatusTag status={value} />,
    },
    {
      field: 'url',
      headerName: texts.history.url,
      flex: 1.4,
      renderCell: ({ value }) => (value
        ? <SiteLink href={value} target="_blank" rel="noopener noreferrer">{value}</SiteLink>
        : EM_DASH),
    },
    {
      field: 'startedBy',
      headerName: texts.history.startedBy,
      flex: 1,
    },
    {
      field: 'createdAt',
      headerName: texts.history.createdAt,
      flex: 1,
      valueFormatter: ({ value }) => (value ? moment(value).format(DateFormat.DATE_FORMAT_TIME_MINUTES) : EM_DASH),
    },
  ], []);

  /*
    A deploy the platform kills never updates its document, so nothing would re-render its row
    when the deadline passes. One timer, for the earliest open deadline, moves `now` forward; the
    re-render derives that row TIMED_OUT and arms the timer for the next.
  */
  const now = useDeadlineNow((renderNow) => nextDeadline(docs, renderNow));
  const rows = docs.map((doc) => ({
    id: doc.id,
    fileName: doc.fileName || EM_DASH,
    siteName: doc.netlifySiteName || EM_DASH,
    status: deriveDeployStatus(doc, now),
    url: doc.url,
    startedBy: doc.startedByName || doc.startedBy || EM_DASH,
    createdAt: doc.createdAt,
  }));

  return (
    <>
      <SectionTitle>{texts.history.title}</SectionTitle>
      <Table
        rows={rows}
        columns={columns}
        loading={deploys.isLoading}
        enableSearch={false}
        pageSize={20}
        disableRowSelectionOnClick
      />
    </>
  );
};

export default observer(DeployHistory);
